import { isMac } from '@/hooks/platform';
import { cn } from '@/lib/cn';
import { useShortcutsHelperList } from '@/lists/hotkeys';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandList,
} from '@heidi/design-system/command';
import { Fade } from '@heidi/design-system/fade';
import { useVerticalScrollFade } from '@heidi/design-system/hooks/use-vertical-scroll-fade';
import { Kbd } from '@heidi/design-system/kbd';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@heidi/design-system/sheet';
import {
  TypographyCaption,
  TypographyP2,
  typographyVariant,
} from '@heidi/design-system/typography';
import { CommandItem } from 'cmdk';
import { useAtom } from 'jotai';
import { useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { getSeparatorClassName } from './shortcuts-helper.utils';
import { isShortcutsSheetOpenAtom } from './shortcutsAtom';

// Mounts with the sheet content so the scroll-fade hook sees a live list node;
// the parent stays mounted while the sheet is closed.
const ShortcutsList = () => {
  const shortcutsHelperList = useShortcutsHelperList();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { canScrollUp, canScrollDown } = useVerticalScrollFade(scrollRef, true);

  return (
    <div className="relative min-h-0 flex-1">
      <CommandList
        ref={scrollRef}
        className="max-h-full pb-4 [&_[cmdk-list-sizer]]:space-y-3"
      >
        {Object.entries(shortcutsHelperList).map(([groupKey, { groupTitle, items }]) => {
          return (
            <CommandGroup
              key={groupTitle}
              className={cn(
                'px-3 py-0 [&_[cmdk-group-heading]]:px-0 [&_[cmdk-group-heading]]:pb-3 [&_[cmdk-group-heading]]:pt-1.5 [&_[cmdk-group-heading]]:text-text-primary [&_[cmdk-group-items]]:space-y-3',
                typographyVariant({ variant: 'p2Bold' })
                  .split(' ')
                  .map((cls) => `[&_[cmdk-group-heading]]:${cls}`)
                  .join(' ')
              )}
              heading={groupTitle}
            >
              {Object.entries(items).map(([, { value, displayedKeys }]) => {
                return (
                  <CommandItem
                    key={value}
                    value={value}
                    className={`flex items-center justify-between py-0`}
                  >
                    <TypographyP2 className="text-text-secondary">{value}</TypographyP2>
                    <div
                      className={`flex items-center ${
                        groupKey === 'goTo' ? 'gap-1.5' : 'gap-[3px]'
                      }`}
                    >
                      {(isMac ? displayedKeys.mac : displayedKeys.windows).map(
                        (item, i) =>
                          item.type === 'separator' ? (
                            <TypographyCaption
                              key={item.key + i + value}
                              className={getSeparatorClassName(item.key)}
                            >
                              {item.key}
                            </TypographyCaption>
                          ) : item.type === 'modifier' ? (
                            <Kbd
                              keyType="modifier"
                              isMac={isMac}
                              tooltip
                              key={item.key + i + value}
                            >
                              {item.key}
                            </Kbd>
                          ) : (
                            <Kbd isMac={isMac} key={item.key + i + value}>
                              {item.key}
                            </Kbd>
                          )
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          );
        })}
      </CommandList>
      {canScrollUp && (
        <Fade
          side="top"
          background="hsl(var(--twc-surface-elevated))"
          blur="6px"
          className="inset-x-0 top-0 h-12"
          data-testid="shortcuts-top-fade"
        />
      )}
      {canScrollDown && (
        <Fade
          side="bottom"
          background="hsl(var(--twc-surface-elevated))"
          blur="6px"
          className="inset-x-0 bottom-0 h-12 rounded-b-xl"
          data-testid="shortcuts-bottom-fade"
        />
      )}
    </div>
  );
};

const ShortcutsHelper = () => {
  const intl = useIntl();
  const [isShortcutsSheetOpen, setIsShortcutsSheetOpen] = useAtom(
    isShortcutsSheetOpenAtom
  );

  const [search, setSearch] = useState('');

  const handleOpenChange = (open: boolean) => {
    if (!open) setSearch('');
    setIsShortcutsSheetOpen(open);
  };

  return (
    <Sheet open={isShortcutsSheetOpen} onOpenChange={handleOpenChange}>
      <SheetContent
        side="left"
        className="gap-2 px-2 pb-0 pt-4"
        data-testid="sidebar-shortcuts-panel"
      >
        <Command className="flex gap-2 bg-transparent">
          <div className="flex flex-col gap-2 px-2">
            <SheetHeader>
              <SheetTitle>
                <FormattedMessage
                  defaultMessage="Keyboard shortcuts"
                  id="aiT0nR229I"
                  description="Title of the panel listing available keyboard shortcuts"
                />
              </SheetTitle>
            </SheetHeader>
            <CommandInput
              wrapperClassName="border border-border rounded-lg"
              className="h-8 py-0"
              value={search}
              onValueChange={setSearch}
              showClearButton
              placeholder={intl.formatMessage({
                defaultMessage: 'Search shortcuts',
                id: '0/z8EuA+Zu',
                description: 'Placeholder for filtering the keyboard shortcuts list',
              })}
            />
          </div>
          <ShortcutsList />
        </Command>
      </SheetContent>
    </Sheet>
  );
};

export default ShortcutsHelper;
